import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import AppContext from '../../appContext';
import Back from '../../main/back';
import { TokenUtils } from "../../shared/utils/TokenUtils";
import { filtersConfig as getFiltersConfig } from "./config/simulador/filters-config";
import { columnsConfig } from "./config/simulador/columns-config";
import InnerPageStructure from '../../shared/components/generic/inner-page-structure/inner-page-structure';
import { useSimulations } from "./hooks/useSimulations";
import Button from '../../shared/components/generic/button/app-button';
import Pagination from "../../shared/components/generic/pagination/pagination";
import TableGeneric from "../../shared/components/generic/table/table-generic";
import { useHistory } from "react-router-dom";
import FormGeneric from '../../shared/components/generic/form/form-generic';
import { clearSimulationMode, saveSimulationMode } from "./utils/simulacao-utils";
import PageStateMessage from '../../shared/components/generic/page-state-message/page-state-message';
import PageLoading from "../../shared/components/generic/page-loading/page-loading";
import { 
	hasValidationErrors,
	removeClearedValidationErrors 
} from '../utils/validation-utils';
import { showCommonAlert } from "../../shared/components/generic/alert/utils/show-common-alert-utils";
import "./simulador.css";

const Simulador = () => {
	const context = useContext(AppContext);
	const history = useHistory();

	const [ hasModuleAccess, setHasModuleAccess ] = useState(false);
	const [appliedFilters, setAppliedFilters] = useState(null);
	const [pagination, setPagination] = useState({
		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 10
	});
	const [simulatorActionLoadingMessage, setSimulatorActionLoadingMessage] = useState("");
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});

	const tableSectionRef = useRef(null);
	const shouldFocusTableRef = useRef(false);

	const {
		simulations,
		validationErrors,
		validateSimulationFilters,
		refetch
	} = useSimulations();

	const filtersConfig = useMemo(() => {
		return getFiltersConfig();
	}, []);

	useEffect(() => {
		if (!shouldFocusTableRef.current) {
			return;
		}

		if (!Array.isArray(simulations) || simulations.length === 0) {
			return;
		}

		shouldFocusTableRef.current = false;

		if (tableSectionRef.current) {
			tableSectionRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start"
			});

			tableSectionRef.current.focus();
		}
	}, [simulations]);

	// Verifica se o usuário tem acesso ao módulo Simulador.
	useEffect(() => {
		setHasModuleAccess(TokenUtils.hasModuloSimulador());
	}, []);

	if (!context.userSession) {
		return (
			<PageStateMessage title="Carregando..." />
		);
	}

	if (!hasModuleAccess) {
		return (
			<PageStateMessage
				title="Acesso Negado"
				description="Você não possui acesso ao módulo Simulador."
			/>
		);
	}

	const handleSimulationFilterFieldChange = ({ name }) => {
		setClearedValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};
	
	async function handleFilterSubmit(filters) {
		setClearedValidationErrors({});

		const validation = validateSimulationFilters(filters);

		if (hasValidationErrors(validation.errors)) {
			const errorMessages = [
				...new Set(Object.values(validation.errors))
			];

			await showCommonAlert({
				title: "Aviso",
				html: errorMessages.join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		try {
			setSimulatorActionLoadingMessage("Buscando simulações...");

			shouldFocusTableRef.current = true;

			const result = await refetch(filters, {
				page: 0,
				size: pagination.size
			});

			if (!result?.ok) {
				shouldFocusTableRef.current = false;

				setPagination(previous => ({
					...previous,
					totalElements: 0,
					totalPages: 0,
					page: 0
				}));

				setSimulatorActionLoadingMessage("");

				await showCommonAlert({
					title: "Aviso",
					message: result?.error,
					fallbackMessage: "Não foi possível recuperar as simulações realizadas. Tente novamente ou entre em contato com o administrador.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			setAppliedFilters({ ...filters });

			if (result?.empty) {
				shouldFocusTableRef.current = false;

				setPagination({
					totalElements: 0,
					totalPages: 0,
					page: 0,
					size: pagination.size
				});

				setSimulatorActionLoadingMessage("");

				await showCommonAlert({
					title: "Aviso",
					message: "Nenhum registro encontrado.",
					icon: "warning",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			setPagination(result.pagination);
		} finally {
			setSimulatorActionLoadingMessage("");
		}
	};

	const handlePageChange = async ({ selected }) => {
		if (!appliedFilters || selected === pagination.page) {
			return;
		}

		try {
			setSimulatorActionLoadingMessage("Buscando simulações...");

			shouldFocusTableRef.current = true;

			const result = await refetch(appliedFilters, {
				page: selected,
				size: pagination.size
			});

			if (!result?.ok) {
				shouldFocusTableRef.current = false;

				await showCommonAlert({
					title: "Erro",
					message: result?.error,
					fallbackMessage: "Não foi possível carregar a página selecionada.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			if (result.empty) {
				shouldFocusTableRef.current = false;
				return;
			}

			setPagination(result.pagination);
		} finally {
			setSimulatorActionLoadingMessage("");
		}
	};

	const handleNewSimulation = () => {
		clearSimulationMode("create");
		saveSimulationMode({
			simulationId: "create",
			mode: "create"
		});

		history.push("/pacto-simulador/simulacao", {
			mode: "create"
		});
	};

	const handleViewSimulation = simulation => {
		saveSimulationMode({
			simulationId: simulation.id,
			mode: "view"
		});

		history.push(`/pacto-simulador/simulacao/${simulation.id}`, {
			mode: "view"
		});
	};

	const handleEditSimulation = simulation => {
		saveSimulationMode({
			simulationId: simulation.id,
			mode: "edit"
		});

		history.push(`/pacto-simulador/simulacao/${simulation.id}`, {
			mode: "edit"
		});
	};

	const simulationColumns = columnsConfig({
		onView: handleViewSimulation,
		onEdit: handleEditSimulation
	});

	const visibleSimulationValidationErrors = removeClearedValidationErrors({
		errors: validationErrors,
		clearedErrors: clearedValidationErrors
	});

	const innerPageElements = {
		backLabel: [{label: "Simulações Realizadas"}],
		title: "Simulações Realizadas",
		headerAction: (
			<Button
				startIcon="plusCircle"
				onClick={handleNewSimulation}
				style={{
					height: "36px",
					padding: "0 18px",
					fontSize: "13px",
					borderRadius: "18px"
				}}
			>
				Nova Simulação
			</Button>
		)
	}

  	return (
		<InnerPageStructure innerPageElements={innerPageElements}>
			<PageLoading
				actionMessage={simulatorActionLoadingMessage}
				states={[]}
			/>
			
			<FormGeneric
				formFields={filtersConfig}
				message="Filtrar Resultados"
				onSubmit={handleFilterSubmit}
				errors={visibleSimulationValidationErrors}
				onFieldChange={handleSimulationFilterFieldChange}
				buttonStartIcon="search"
				buttonEndIcon="arrowRight"
				buttonIconLayout="edge"
			/>

			{Array.isArray(simulations) && simulations.length > 0 && (
				<div
					ref={tableSectionRef}
					className="simulador__table-section"
					tabIndex={-1}
				>
					<TableGeneric
						columns={simulationColumns}
						data={simulations}
						rowKey="id"
					/>
				</div>
			)}

			{Array.isArray(simulations) && simulations.length > 0 && pagination.totalPages > 1 && (
				<Pagination
					pageCount={pagination.totalPages}
					currentPage={pagination.page}
					onPageChange={handlePageChange}
				/>
			)}
		</InnerPageStructure>
  	);
};

export default Simulador;